import { toast } from 'sonner';
import type { Task } from '@timato/shared';
import { db } from '@/db/db';
import { saveRow } from '@/data/repo';
import { useAuth } from '@/auth/store';
import { ApiError } from '@/auth/api';
import { teamApi } from './api';
import { useTeam } from './store';

const PENDING_KEY = 'team.pendingClaims';
export type LockOutcome = { ok: true } | { ok: false; holderName: string | null };

/** Which team a task belongs to (null for personal tasks). */
export async function teamOfTask(task: Task | null | undefined): Promise<string | null> {
  return task ? (await db.projects.get(task.projectId))?.teamId ?? null : null;
}

/**
 * Hard lock (spec §4.2, decision 14): online, claim before starting and refuse on 409. Offline, start optimistically
 * and remember the claim; `reconcilePendingClaims` settles it on reconnect.
 */
export async function claimForStart(task: Task, teamId: string): Promise<LockOutcome> {
  if (!navigator.onLine) { await rememberPending(task.id); return { ok: true }; }
  try {
    await teamApi.claim(teamId, task.id);
    await db.tasks.update(task.id, { claimedBy: useAuth.getState().user?.id ?? null, claimedAt: new Date().toISOString() });
    return { ok: true };
  } catch (e) {
    if (e instanceof ApiError && e.status === 409) { const holder = (e.body?.holderName as string | undefined) ?? null; return { ok: false, holderName: holder }; }
    await rememberPending(task.id); // network hiccup: behave like offline
    return { ok: true };
  }
}

export async function releaseTask(task: Task) {
  const teamId = await teamOfTask(task);
  if (!teamId) return;
  await teamApi.release(teamId, task.id);
  await db.tasks.update(task.id, { claimedBy: null, claimedAt: null });
  void useTeam.getState().refresh();
}

async function rememberPending(taskId: string) {
  const list = ((await db.meta.get(PENDING_KEY))?.value as string[] | undefined) ?? [];
  if (!list.includes(taskId)) await db.meta.put({ key: PENDING_KEY, value: [...list, taskId] });
}

/** After reconnecting: claims that were made offline either stick or lose; a lost claim re-attributes to the project (8m). */
export async function reconcilePendingClaims() {
  const list = ((await db.meta.get(PENDING_KEY))?.value as string[] | undefined) ?? [];
  if (!list.length || !navigator.onLine) return;
  for (const taskId of list) {
    const task = await db.tasks.get(taskId);
    const teamId = await teamOfTask(task);
    if (!task || !teamId) continue;
    const r = await claimForStart(task, teamId);
    if (r.ok) continue;
    const timer = await db.activeTimer.get('active');
    if (timer?.taskId === taskId) await saveRow('activeTimer', { ...timer, taskId: null });
    const sessions = (await db.sessions.where('taskId').equals(taskId).toArray()).filter((s) => s.deletedAt == null && Date.parse(s.startedAt) > Date.now() - 7 * 86_400_000);
    for (const s of sessions) await saveRow('sessions', { ...s, taskId: null });
    toast('Task claimed by someone else', { description: `${r.holderName ?? 'A teammate'} claimed “${task.name}” while you were offline. Your time is logged to the project instead.` });
  }
  await db.meta.delete(PENDING_KEY);
}
